import React, { useState, useCallback } from 'react'
import InlineEdit from '@atlaskit/inline-edit'
import TextField from '@atlaskit/textfield'
import TextArea from '@atlaskit/textarea'
import { token } from '@atlaskit/tokens'
import Spinner from '@atlaskit/spinner'

/**
 * INLINE EDIT FIELD COMPONENT
 *
 * Jira-style inline edit field that saves immediately on confirm.
 * Supports both single-line (TextField) and multi-line (TextArea) modes.
 *
 * Features:
 * - Required field validation with error message
 * - Loading state during async save
 * - Error handling with visual feedback
 * - Keeps edit mode open if validation fails
 *
 * @param {string} label - Field label displayed above the input
 * @param {string} value - Current value to display/edit
 * @param {function} onConfirm - Async callback when user confirms edit (receives new value)
 * @param {boolean} multiline - Use TextArea (true) or TextField (false)
 * @param {string} placeholder - Placeholder text when value is empty
 * @param {number} rows - Number of rows for TextArea (when multiline=true)
 * @param {boolean} required - Show required indicator and validate non-empty
 * @param {string} requiredMessage - Custom validation message for required field
 */
export function InlineEditField({
  label,
  value,
  onConfirm,
  multiline = false,
  placeholder = 'Click to edit',
  rows = 3,
  required = false,
  requiredMessage = 'This field is required'
}) {
  // Track loading state during async save
  const [isLoading, setIsLoading] = useState(false)

  // Track validation error
  const [validationError, setValidationError] = useState('')

  // Key to force InlineEdit re-render when value changes externally
  const inlineEditKey = `${label}-${value ?? ''}`

  /**
   * VALIDATE FIELD
   * Returns error message if validation fails, empty string if valid
   */
  const validate = useCallback((newValue) => {
    if (required && (!newValue || !newValue.trim())) {
      return requiredMessage
    }
    return ''
  }, [required, requiredMessage])

  /**
   * HANDLE CONFIRM
   * Called when user confirms the edit (Enter key or checkmark click).
   * Validates the value, then calls onConfirm if valid.
   */
  const handleConfirm = useCallback(async (newValue) => {
    // Validate first
    const error = validate(newValue)
    if (error) {
      setValidationError(error)
      // Don't save - keep edit mode open
      return
    }

    // Clear any previous validation error
    setValidationError('')

    // Skip if value hasn't changed
    if (newValue === value) {
      return
    }

    // Show loading state
    setIsLoading(true)

    try {
      // Call the async save handler
      await onConfirm(newValue)
    } catch (error) {
      console.error('Failed to save field:', error)
      // Error is handled by parent via notification
    } finally {
      setIsLoading(false)
    }
  }, [onConfirm, validate, value])

  /**
   * RENDER READ VIEW
   * Shows the current value or placeholder when not editing
   */
  const renderReadView = useCallback(() => (
    <div
      style={{
        padding: '8px 6px',
        minHeight: multiline ? '60px' : '32px',
        color: value ? token('color.text') : token('color.text.subtlest'),
        cursor: 'pointer',
        whiteSpace: multiline ? 'pre-wrap' : 'normal',
        wordBreak: 'break-word',
        borderRadius: '3px',
        background: 'transparent',
        transition: 'background 0.1s ease',
        position: 'relative'
      }}
      className="inline-edit-read-view"
    >
      {/* Show spinner overlay when loading */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            padding: '6px'
          }}
        >
          <Spinner size="small" />
        </div>
      )}
      {value || placeholder}
    </div>
  ), [value, placeholder, multiline, isLoading])

  /**
   * RENDER EDIT VIEW
   * Shows the input field (TextField or TextArea) when editing
   */
  const renderEditView = useCallback(({ errorMessage, ...fieldProps }) => {
    // Combine external errorMessage with our validation error
    const displayError = validationError || errorMessage

    return (
      <div style={{ position: 'relative' }}>
        {multiline ? (
          <TextArea
            {...fieldProps}
            autoFocus
            placeholder={placeholder}
            minimumRows={rows}
            isInvalid={!!displayError}
            onFocus={(e) => {
              // Call InlineEdit's onFocus if present
              fieldProps.onFocus?.(e)
              // Move cursor to end of text
              const el = e.target
              requestAnimationFrame(() => {
                el.selectionStart = el.selectionEnd = el.value.length
              })
            }}
          />
        ) : (
          <TextField
            {...fieldProps}
            autoFocus
            placeholder={placeholder}
            isInvalid={!!displayError}
          />
        )}
        {/* Show validation error below field */}
        {displayError && (
          <div
            style={{
              font: token('font.body.small'),
              color: token('color.text.danger'),
              marginTop: '4px'
            }}
          >
            {displayError}
          </div>
        )}
      </div>
    )
  }, [multiline, rows, validationError, placeholder])

  return (
    <div style={{ marginBottom: '20px' }}>
      {/* Field label */}
      <label
        className="inline-edit-label"
        style={{
          display: 'block',
          marginBottom: 'var(--ds-space-050, 4px)'
        }}
      >
        {label}
        {required && <span style={{ color: token('color.text.danger', '#DE350B') }}> *</span>}
      </label>

      {/* Inline edit component */}
      <InlineEdit
        key={inlineEditKey}
        defaultValue={value || ''}
        editView={renderEditView}
        readView={renderReadView}
        onConfirm={handleConfirm}
        // Keep edit mode open when validation fails
        keepEditViewOpenOnBlur={!!validationError}
        // Hide default action buttons - Enter/Escape work instead
        hideActionButtons={false}
      />
    </div>
  )
}
